import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { EditorProvider, useEditor } from "./context/EditorContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { WannaPublishLockup } from "./components/editor/WannaPublishLogo";
import Editor from "./pages/Editor";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import Subscribe from "./pages/Subscribe";
import SubscribeSuccess from "./pages/SubscribeSuccess";
import Legal from "./pages/Legal";
import Help from "./pages/Help";
import About from "./pages/About";
import Unsubscribe from "./pages/Unsubscribe";
import Owner from "./pages/Owner";
import OwnerEngine from "./pages/OwnerEngine";

/** The editor with its own theme wrapper (the editor state owns the light / dark toggle). */
const ThemedEditor = () => {
  const { state } = useEditor();
  return (
    <div className={state.theme === 'dark' ? 'dark' : ''}>
      <Editor />
    </div>
  );
};

/** Signed-in users only: while the session is being checked show a quiet splash; signed out goes to the landing page (keeping a share link's ?project=). */
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--wp-workspace))] flex items-center justify-center" aria-busy="true" data-testid="auth-loading">
        <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground"><WannaPublishLockup size={44} pulse /><span>Opening WannaPublish…</span></div>
      </div>
    );
  }
  if (!user) return <Navigate to={`/${location.search || ''}`} replace />;
  return children;
};

/**
 * Subscribers only (WannaPublish is paid-only). `editor_access` comes from /auth/me: true while a trial / active /
 * past-due subscription exists — or when billing is not configured on the server. Everyone else picks a plan first;
 * the share link they followed (?share=…) travels along and is honoured once they are in.
 */
const RequireSubscription = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user && user.editor_access === false) return <Navigate to={`/subscribe${location.search || ''}`} replace />;
  return children;
};

/** Owner accounts only (`is_owner` from /auth/me, OWNER_EMAILS on the server); everyone else lands in the editor. */
const RequireOwner = ({ children }) => {
  const { user } = useAuth();
  if (user && !user.is_owner) return <Navigate to="/app" replace />;
  return children;
};

const AppRouter = () => {
  const location = useLocation(); // read the hash from here, not window.location.hash (not reactive)
  // Google sign-in return: the URL fragment carries session_id — exchange it before any route / auth check runs
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      <Route path="/help" element={<Help />} />
      <Route path="/about" element={<About />} />
      <Route path="/subscribe" element={<RequireAuth><Subscribe /></RequireAuth>} />
      <Route path="/subscribe/success" element={<RequireAuth><SubscribeSuccess /></RequireAuth>} />
      <Route path="/owner" element={<RequireAuth><RequireOwner><Owner /></RequireOwner></RequireAuth>} />
      <Route path="/owner/engine" element={<RequireAuth><RequireOwner><OwnerEngine /></RequireOwner></RequireAuth>} />
      <Route
        path="/app"
        element={(
          <RequireAuth>
            <RequireSubscription>
              <EditorProvider>
                <ThemedEditor />
              </EditorProvider>
            </RequireSubscription>
          </RequireAuth>
        )}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </AuthProvider>
  );
}

export default App;
